<!--
  新クロージングページの「完了」ステップ。
  クロージング完了のねぎらいと、その日の主要 KPI（売上合計 / 現金売上 /
  来客数 / 客単価）を前日比つきで表示する。
  KPI は日報βと同じ収支 API（FinanceApi / SalesApi）から、当日分と前日分を
  取得して算出する。
-->
<template>
  <v-card
    flat
    class="complete-card pa-8"
  >
    <!-- 未完了: 集計が完了していない -->
    <div
      v-if="!completed"
      class="text-center"
    >
      <v-icon
        color="warning"
        size="48"
        class="mb-3"
      >
        {{ icons.mdiAlertCircleOutline }}
      </v-icon>
      <p class="incomplete-message mb-2">
        まだクロージングが完了していません
      </p>
      <p class="incomplete-note mb-0">
        未集計・未保存の集計表があります。各ステップを確認し、集計と保存を完了させてください。
      </p>
    </div>

    <!-- 完了: クロージング完了サマリー -->
    <template v-else>
      <p class="complete-message text-center mb-6">
        お疲れさまでした。<strong>{{ formattedDate }}</strong> のクロージングが完了しました。
      </p>

      <v-skeleton-loader
        v-if="loading"
        type="image"
        class="mb-8"
      />
      <div
        v-else
        class="kpi-grid mb-8"
      >
        <div
          v-for="kpi in kpis"
          :key="kpi.key"
          class="kpi-card"
        >
          <div class="kpi-label">
            {{ kpi.label }}
          </div>
          <div
            class="kpi-value"
            :class="`${kpi.color}--text`"
          >
            {{ kpi.value }}
          </div>
          <div
            class="kpi-delta"
            :class="kpi.deltaClass"
          >
            {{ kpi.delta }}
          </div>
        </div>
      </div>

      <div class="text-center">
        <v-btn
          color="primary"
          x-large
          :ripple="false"
          :to="{ name: 'daily-report-summary', query: { date } }"
        >
          日報を見る
        </v-btn>
        <p class="complete-note mt-3 mb-0">
          このサマリーは日報からいつでも確認できます
        </p>
      </div>
    </template>
  </v-card>
</template>

<script>
import {
  ref, reactive, computed, watch, onMounted,
} from '@vue/composition-api'
import { parseISO, subDays, format } from 'date-fns'
import { mdiAlertCircleOutline } from '@mdi/js'
import SalesApi from '@/api/admin/Sales'
import FinanceApi from '@/api/admin/Finance'

export default {
  name: 'EodClosingCompleteStep',
  props: {
    // 対象営業日 (YYYY-MM-DD)
    date: {
      type: String,
      default: '',
    },
    // 全カテゴリのクロージングが完了しているか
    completed: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const loading = ref(false)
    const current = reactive({ sales: 0, cash: 0, customers: 0 })
    const previous = reactive({ sales: 0, cash: 0, customers: 0 })

    const formattedDate = computed(() => {
      const parts = (props.date || '').split('-')
      if (parts.length !== 3) return props.date || ''

      return `${Number(parts[0])}年${Number(parts[1])}月${Number(parts[2])}日`
    })

    // 指定日の売上合計・現金売上・来客数を収支 API から取得する。
    const fetchFor = async dateStr => {
      const [income, perf] = await Promise.all([
        FinanceApi.getIncomeDetail({ date: dateStr, interval: 0, endDate: '' }),
        SalesApi.getPerformance({ date: dateStr, interval: 0, endDate: '' }),
      ])
      const cash = Number(income?.data?.cashAmount || 0)
      const card = Number(income?.data?.cardAmount || 0)
      const receivable = Number(income?.data?.balanceReceivableAmount || 0)
      const customers = Number(perf?.data?.performance?.customerCount || 0)

      return { sales: cash + card + receivable, cash, customers }
    }

    // 当日と前日の KPI をまとめて取得する。
    const load = async () => {
      if (!props.date) return
      loading.value = true
      try {
        const prevDate = format(subDays(parseISO(props.date), 1), 'yyyy-MM-dd')
        const [cur, prev] = await Promise.all([fetchFor(props.date), fetchFor(prevDate)])
        Object.assign(current, cur)
        Object.assign(previous, prev)
      } catch (e) {
        // KPI は付加表示のため、取得失敗時は 0 のままにする。
      } finally {
        loading.value = false
      }
    }

    onMounted(load)
    watch(() => props.date, load)

    const yen = n => `¥${Number(n || 0).toLocaleString()}`

    // 前日比（％）。前日が 0 のときは比較を表示しない。
    const pctDelta = (cur, prev) => {
      if (!prev) return { delta: '― vs 前日', deltaClass: 'delta-flat' }
      const pct = Math.round(((cur - prev) / prev) * 100)
      if (pct > 0) return { delta: `↑ ${pct}% vs 前日`, deltaClass: 'delta-up' }
      if (pct < 0) return { delta: `↓ ${Math.abs(pct)}% vs 前日`, deltaClass: 'delta-down' }

      return { delta: '→ 0% vs 前日', deltaClass: 'delta-flat' }
    }

    // 前日比（実数差）。来客数用。
    const countDelta = (cur, prev) => {
      const diff = cur - prev
      if (diff > 0) return { delta: `↑ ${diff} vs 前日`, deltaClass: 'delta-up' }
      if (diff < 0) return { delta: `↓ ${Math.abs(diff)} vs 前日`, deltaClass: 'delta-down' }

      return { delta: '→ 0 vs 前日', deltaClass: 'delta-flat' }
    }

    const kpis = computed(() => {
      const curAvg = current.customers > 0 ? Math.round(current.sales / current.customers) : 0
      const prevAvg = previous.customers > 0 ? Math.round(previous.sales / previous.customers) : 0

      return [
        {
          key: 'sales',
          label: '売上合計',
          value: yen(current.sales),
          color: 'primary',
          ...pctDelta(current.sales, previous.sales),
        },
        {
          key: 'cash',
          label: '現金売上',
          value: yen(current.cash),
          color: 'success',
          ...pctDelta(current.cash, previous.cash),
        },
        {
          key: 'customers',
          label: '来客数',
          value: String(current.customers),
          color: 'info',
          ...countDelta(current.customers, previous.customers),
        },
        {
          key: 'avg',
          label: '客単価',
          value: yen(curAvg),
          color: 'primary',
          ...pctDelta(curAvg, prevAvg),
        },
      ]
    })

    return {
      loading,
      formattedDate,
      kpis,
      icons: {
        mdiAlertCircleOutline,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.complete-card {
  background: linear-gradient(135deg, rgba(145, 85, 253, .16), rgba(145, 85, 253, .03)) !important;
  border: 1px solid rgba(145, 85, 253, .28) !important;
  border-radius: 14px;
}

.complete-message {
  font-size: 18px;
  opacity: .9;

  strong {
    font-weight: 700;
  }
}

.incomplete-message {
  font-size: 18px;
  font-weight: 700;
}

.incomplete-note {
  font-size: 13px;
  opacity: .7;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

@media (max-width: 760px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.kpi-card {
  padding: 18px 12px;
  text-align: center;
  background: rgba(255, 255, 255, .03);
  border: 1px solid rgba(255, 255, 255, .08);
  border-radius: 10px;
}

.kpi-label {
  margin-bottom: 6px;
  font-size: 13px;
  opacity: .6;
}

.kpi-value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
}

.kpi-delta {
  margin-top: 6px;
  font-size: 12px;
}

.delta-up {
  color: var(--v-success-base);
}

.delta-down {
  color: var(--v-error-base);
}

.delta-flat {
  opacity: .5;
}

.complete-note {
  font-size: 12px;
  opacity: .5;
}
</style>
